import { useEffect, useState } from 'react';
import type { CompanySettings, CompanySettingsResponse, InsightRule } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import Hint from './Hint';
import Loading from './Loading';
import { parseAmount } from './money';

type DashboardSettingsValue = CompanySettings['dashboard'];

const RULE_LABELS: Record<InsightRule, string> = {
  overdue_customer: 'العملاء المتأخرون',
  cash_runway: 'مدة كفاية النقدية',
  net_loss: 'خسارة الفترة',
  expense_spike: 'زيادة المصروفات',
  margin_change: 'تغير هامش الربح',
  revenue_change: 'تغير الإيراد',
  supplier_due_soon: 'موردون مستحقون قريبًا',
  large_unpaid_invoices: 'فواتير كبيرة غير محصّلة',
  slow_moving_stock: 'أصناف راكدة',
  vat_due: 'ضريبة القيمة المضافة المستحقة',
  best_sales_day: 'أفضل يوم مبيعات',
};

const MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

function NumberField({
  label,
  hint,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="dash-field">
      <span>{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(event) => {
          const next = Math.round(Number(event.target.value));
          if (Number.isFinite(next)) onChange(Math.min(max, Math.max(min, next)));
        }}
      />
      {hint && <Hint>{hint}</Hint>}
    </label>
  );
}

/**
 * تعريفات اللوحة وحدود تنبيهاتها — جزء من إعدادات الشركة (ADR-0006)، تُحفظ كاملة مع
 * بقية الإعدادات عبر نفس المسار وبصلاحية `settings.manage`، فتدخل سجل التدقيق.
 */
export default function DashboardSettings({
  companyId,
  onSaved,
  onClose,
  onSessionLost,
}: {
  companyId: string;
  onSaved: () => void;
  onClose: () => void;
  onSessionLost: () => void;
}) {
  const [loaded, setLoaded] = useState<CompanySettingsResponse | null>(null);
  const [value, setValue] = useState<DashboardSettingsValue | null>(null);
  const [amount, setAmount] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .companySettings(companyId)
      .then((result) => {
        if (cancelled) return;
        setLoaded(result);
        setValue(result.settings.dashboard);
        setAmount(result.settings.dashboard.insights.largeInvoiceAmount);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) onSessionLost();
        else setFailure(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, onSessionLost]);

  if (!value || !loaded) {
    return (
      <div className="dash-settings">
        {failure ? <p className="error">{failure}</p> : <Loading lines={4} />}
      </div>
    );
  }

  const insights = value.insights;
  const runway = value.runway;
  const setInsights = (patch: Partial<DashboardSettingsValue['insights']>) =>
    setValue({ ...value, insights: { ...insights, ...patch } });
  const setRunway = (patch: Partial<DashboardSettingsValue['runway']>) =>
    setValue({ ...value, runway: { ...runway, ...patch } });

  async function save() {
    if (!value || !loaded) return;
    const parsed = parseAmount(amount);
    if (parsed === null || parsed === '0') {
      setFailure('حد الفاتورة الكبيرة مبلغ موجب');
      return;
    }
    setSaving(true);
    setFailure(null);
    try {
      await api.updateCompanySettings(companyId, {
        profile: loaded.profile,
        settings: {
          ...loaded.settings,
          dashboard: {
            ...value,
            insights: { ...value.insights, largeInvoiceAmount: parsed },
          },
        },
      });
      onSaved();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else
        setFailure(
          caught instanceof ProblemError
            ? (caught.problem.detail ?? caught.problem.title)
            : caught instanceof Error
              ? caught.message
              : String(caught),
        );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dash-settings">
      <div className="dash-settings-head">
        <h3>إعدادات التنبيهات والمقارنة</h3>
        <button type="button" className="ghost" onClick={onClose}>
          إغلاق
        </button>
      </div>
      <p className="sub">
        تغيّر طريقة قراءة الأرقام وحدود التنبيه فقط — لا تمس قيدًا ولا رقمًا منشورًا.
      </p>

      <fieldset>
        <legend>المقارنة والفترات</legend>
        <label className="dash-field">
          <span>قارن الفترة بـ</span>
          <select
            value={value.comparison}
            onChange={(event) =>
              setValue({
                ...value,
                comparison: event.target.value as DashboardSettingsValue['comparison'],
              })
            }
          >
            <option value="previous_period">الفترة السابقة بنفس الطول (موصى به)</option>
            <option value="same_period_last_year">نفس الفترة من العام الماضى</option>
          </select>
        </label>
        <label className="dash-field">
          <span>بداية السنة المالية</span>
          <select
            value={value.fiscalYearStartMonth}
            onChange={(event) =>
              setValue({ ...value, fiscalYearStartMonth: Number(event.target.value) })
            }
          >
            {MONTHS.map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </fieldset>

      <fieldset>
        <legend>كفاية النقدية</legend>
        <label className="dash-field">
          <span>معدل الصرف</span>
          <select
            value={runway.basis}
            onChange={(event) =>
              setRunway({ basis: event.target.value as DashboardSettingsValue['runway']['basis'] })
            }
          >
            <option value="net_burn">صافى: المدفوع − المحصّل (موصى به)</option>
            <option value="gross_burn">إجمالى المدفوع (أحوط: لو توقف التحصيل)</option>
          </select>
        </label>
        <NumberField
          label="متوسط آخر (شهر كامل)"
          value={runway.lookbackMonths}
          min={1}
          max={12}
          onChange={(lookbackMonths) => setRunway({ lookbackMonths })}
        />
        <NumberField
          label="تحذير تحت (شهر)"
          value={runway.warningMonths}
          min={1}
          max={36}
          onChange={(warningMonths) => setRunway({ warningMonths })}
        />
        <NumberField
          label="عاجل تحت (شهر)"
          value={runway.criticalMonths}
          min={0}
          max={36}
          onChange={(criticalMonths) => setRunway({ criticalMonths })}
        />
      </fieldset>

      <fieldset>
        <legend>حدود التنبيه</legend>
        <NumberField
          label="تغير الإيراد ٪"
          value={insights.revenueChangePercent}
          min={1}
          max={500}
          onChange={(revenueChangePercent) => setInsights({ revenueChangePercent })}
        />
        <NumberField
          label="زيادة المصروفات ٪"
          value={insights.expenseIncreasePercent}
          min={1}
          max={500}
          onChange={(expenseIncreasePercent) => setInsights({ expenseIncreasePercent })}
        />
        <NumberField
          label="تغير هامش الربح (نقطة)"
          value={insights.marginChangePoints}
          min={1}
          max={100}
          onChange={(marginChangePoints) => setInsights({ marginChangePoints })}
        />
        <label className="dash-field">
          <span>العميل متأخر بعد</span>
          <select
            value={insights.overdueCustomerDays}
            onChange={(event) =>
              setInsights({ overdueCustomerDays: Number(event.target.value) as 30 | 60 | 90 })
            }
          >
            <option value={30}>٣٠ يومًا</option>
            <option value={60}>٦٠ يومًا</option>
            <option value={90}>٩٠ يومًا</option>
          </select>
        </label>
        <NumberField
          label="الصنف راكد بعد (يوم بلا بيع)"
          value={insights.slowMovingDays}
          min={7}
          max={730}
          onChange={(slowMovingDays) => setInsights({ slowMovingDays })}
        />
        <label className="dash-field">
          <span>فاتورة كبيرة من</span>
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <NumberField
          label="موردون مستحقون خلال (يوم)"
          value={insights.supplierDueDays}
          min={1}
          max={60}
          onChange={(supplierDueDays) => setInsights({ supplierDueDays })}
        />
        <label className="dash-field">
          <span>موعد إقرار القيمة المضافة</span>
          <select
            value={insights.vatReturnDueDay ?? ''}
            onChange={(event) =>
              setInsights({
                vatReturnDueDay: event.target.value === '' ? null : Number(event.target.value),
              })
            }
          >
            <option value="">آخر يوم فى الشهر التالى</option>
            {Array.from({ length: 31 }, (_, index) => (
              <option key={index + 1} value={index + 1}>
                يوم {index + 1} من الشهر التالى
              </option>
            ))}
          </select>
          <small>راجعه مع مستشار الضرائب.</small>
        </label>
      </fieldset>

      <fieldset>
        <legend>التنبيهات الظاهرة</legend>
        <div className="dash-rule-toggles">
          {(Object.keys(RULE_LABELS) as InsightRule[]).map((rule) => (
            <label key={rule} className="dash-toggle">
              <input
                type="checkbox"
                checked={!insights.hidden.includes(rule)}
                onChange={(event) =>
                  setInsights({
                    hidden: event.target.checked
                      ? insights.hidden.filter((item) => item !== rule)
                      : [...insights.hidden, rule],
                  })
                }
              />
              {RULE_LABELS[rule]}
            </label>
          ))}
        </div>
      </fieldset>

      {failure && <p className="error">{failure}</p>}
      <div className="dash-settings-actions">
        <button type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'جارى الحفظ…' : 'حفظ'}
        </button>
      </div>
    </div>
  );
}
